import { Export, FusionError, RExport, RImport, safeParseJson, serializeFusionError } from '@fusion-advantage/backcompat'
import { draftExportApplyCodeNextUpdate, draftExportApplyImport, REncounterCodeNextUpdate } from "@fusion-advantage/formlib"
import { err, ok, Result } from "neverthrow"
import { CloudToDeviceBlobType, selectCloudToDeviceBlob } from "../dal/ldbCloudToDeviceBlob"
import { DeviceToCloudBlobType, selectLatestDeviceToCloudBlob } from "../dal/ldbDeviceToCloudBlob"

interface mustSelectExportOk {
	exp: Export
	pendingSync: boolean
}

// "must" because null is an error;
// we are expecting this value to be in the table
export const mustSelectExport = async (upn: string, encounterGuid: string): Promise<Result<mustSelectExportOk, FusionError>> => {
	const rSelect = await selectCloudToDeviceBlob({
		type: CloudToDeviceBlobType.Export,
		id: encounterGuid,
		upn,
	})
	if(rSelect.isErr()) {
		return err(serializeFusionError('mustSelectExport', rSelect.error))
	}
	if(!rSelect.value) {
		return err(serializeFusionError('mustSelectExport', 'null export for work item'))
	}
	const rParse = safeParseJson(rSelect.value.data, RExport)
	if(rParse.isErr()) {
		return err(serializeFusionError(`parse export | mustSelectExport`, rParse.error))
	}
	let exp = rParse.value
	let pendingSync = false
	// check local encounter status updates
	// to see if they should override the export
	const rSelectEsUpdate = await selectLatestDeviceToCloudBlob({
		type: DeviceToCloudBlobType.EncounterStatus,
		encounterGuid,
		fileId: null,
		upn,
	})
	if(rSelectEsUpdate.isErr()) {
		return err(serializeFusionError('mustSelectExport', rSelectEsUpdate.error))
	}
	// if there is a local encounter status update,
	// use it to override if it is NOT synced,
	// or if synced, override if we haven't received it back
	// in a CloudToDevice sync yet
	if(
		rSelectEsUpdate.value &&
		(
			(!rSelectEsUpdate.value.syncedAt) ||
			(rSelectEsUpdate.value.createdAt > rSelect.value.syncedAt)
		)
	) {
		pendingSync = true
		const rParse = safeParseJson(rSelectEsUpdate.value.data, REncounterCodeNextUpdate)
		if(rParse.isErr()) {
			return err(serializeFusionError('parse esUpdate | mustSelectExport', rParse.error))
		}
		const codeNextUpdate = rParse.value
		exp = draftExportApplyCodeNextUpdate(exp, codeNextUpdate)
	}
	// check local imports
	// to see if they should override the export
	const rSelectImport = await selectLatestDeviceToCloudBlob({
		type: DeviceToCloudBlobType.Import,
		encounterGuid,
		fileId: null,
		upn,
	})
	if(rSelectImport.isErr()) {
		return err(serializeFusionError('mustSelectExport', rSelectImport.error))
	}
	// if there is a local import,
	// use it to override if it is NOT synced,
	// or if synced, override if we haven't received it back
	// in a CloudToDevice sync yet
	if(
		rSelectImport.value &&
		(
			(!rSelectImport.value.syncedAt) ||
			(rSelectImport.value.createdAt > rSelect.value.syncedAt)
		)
	) {
		pendingSync = true
		const rParse = safeParseJson(rSelectImport.value.data, RImport)
		if(rParse.isErr()) {
			return err(serializeFusionError('parse import | mustSelectExport', rParse.error))
		}
		const imp = rParse.value
		exp = draftExportApplyImport(exp, imp)
	}
	return ok({exp, pendingSync})
}
